import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Package, Eye, Download, FileText, ChevronRight, Truck, Store, MapPin, CreditCard, Clock, CheckCircle, Bell, BellOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  pending: 'bg-orange-100 text-orange-700 border-orange-300',
  confirmed: 'bg-blue-100 text-blue-700 border-blue-300',
  processing: 'bg-purple-100 text-purple-700 border-purple-300',
  shipped: 'bg-cyan-100 text-cyan-700 border-cyan-300',
  delivered: 'bg-green-100 text-green-700 border-green-300',
  cancelled: 'bg-red-100 text-red-700 border-red-300'
};

const statusLabels = {
  pending: 'En attente',
  confirmed: 'Confirmée',
  processing: 'En préparation',
  shipped: 'Expédiée',
  delivered: 'Livrée',
  cancelled: 'Annulée'
};

const paymentLabels = {
  paid: 'Payé',
  pending: 'En attente',
  cod_pending: 'À la livraison'
};

// Receipt Component
const OrderReceipt = ({ order, onClose }) => {
  if (!order) return null;

  const downloadReceipt = () => {
    // Download PDF from backend
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/api/orders/${order.order_id}/receipt-pdf`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    .then(response => {
      if (!response.ok) throw new Error('Failed to download receipt');
      return response.blob();
    })
    .then(blob => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `recu_alina_${order.order_id.slice(-8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    })
    .catch(error => {
      console.error('Error downloading receipt:', error);
      // Fallback to HTML if PDF fails
      downloadReceiptHTML();
    });
  };

  const downloadReceiptHTML = () => {
    const receiptHTML = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Reçu ${order.order_id}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; background: #f5f5f5; padding: 20px; }
    .receipt { max-width: 600px; margin: 0 auto; background: white; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
    .header { background: linear-gradient(135deg, #1a1a1a 0%, #333 100%); color: #d4af37; padding: 30px; text-align: center; }
    .header h1 { font-size: 28px; letter-spacing: 4px; margin-bottom: 5px; }
    .header p { color: #fff; font-size: 12px; letter-spacing: 2px; }
    .gold-bar { height: 4px; background: linear-gradient(90deg, #d4af37, #f4e4bc, #d4af37); }
    .content { padding: 30px; }
    .order-info { background: #f9f9f9; padding: 15px; border-radius: 8px; margin-bottom: 25px; border-left: 4px solid #d4af37; }
    .order-info h2 { color: #1a1a1a; font-size: 14px; margin-bottom: 10px; }
    .order-info p { color: #666; font-size: 13px; margin: 5px 0; }
    .items-title { color: #d4af37; font-size: 14px; font-weight: bold; letter-spacing: 2px; margin-bottom: 15px; border-bottom: 2px solid #d4af37; padding-bottom: 8px; }
    .item { display: flex; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid #eee; }
    .item-name { color: #333; flex: 1; }
    .item-qty { color: #888; width: 80px; text-align: center; }
    .item-price { color: #d4af37; font-weight: bold; width: 100px; text-align: right; }
    .totals { margin-top: 20px; padding: 20px; background: #1a1a1a; border-radius: 8px; }
    .total-row { display: flex; justify-content: space-between; color: #fff; padding: 8px 0; }
    .total-row.grand { border-top: 2px solid #d4af37; margin-top: 10px; padding-top: 15px; font-size: 18px; }
    .total-row.grand span:last-child { color: #d4af37; }
    .shipping-info { margin-top: 25px; padding: 20px; background: #f9f9f9; border-radius: 8px; }
    .shipping-info h3 { color: #1a1a1a; font-size: 13px; margin-bottom: 10px; letter-spacing: 1px; }
    .shipping-info p { color: #666; font-size: 13px; margin: 3px 0; }
    .footer { background: #1a1a1a; color: #888; padding: 20px; text-align: center; font-size: 12px; }
    .footer p { margin: 5px 0; }
    .footer .gold { color: #d4af37; }
  </style>
</head>
<body>
  <div class="receipt">
    <div class="header">
      <h1>ALINA ELEGANCE</h1>
      <p>REÇU DE COMMANDE</p>
    </div>
    <div class="gold-bar"></div>
    <div class="content">
      <div class="order-info">
        <h2>INFORMATIONS DE COMMANDE</h2>
        <p><strong>N° Commande:</strong> #${order.order_id.slice(-8).toUpperCase()}</p>
        <p><strong>Date:</strong> ${new Date(order.created_at).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        <p><strong>Statut:</strong> ${statusLabels[order.order_status] || order.order_status}</p>
        <p><strong>Paiement:</strong> ${paymentLabels[order.payment_status] || order.payment_status}</p>
      </div>
      
      <div class="items-title">ARTICLES COMMANDÉS</div>
      ${order.items.map(item => `
        <div class="item">
          <span class="item-name">${item.name}</span>
          <span class="item-qty">× ${item.quantity}</span>
          <span class="item-price">$${(item.price * item.quantity).toFixed(2)}</span>
        </div>
      `).join('')}
      
      <div class="totals">
        <div class="total-row">
          <span>Sous-total</span>
          <span>$${order.subtotal?.toFixed(2) || '0.00'}</span>
        </div>
        <div class="total-row">
          <span>Livraison</span>
          <span>$${order.delivery_fee?.toFixed(2) || '0.00'}</span>
        </div>
        <div class="total-row grand">
          <span>TOTAL</span>
          <span>$${order.total.toFixed(2)} CAD</span>
        </div>
      </div>
      
      ${order.shipping_address ? `
      <div class="shipping-info">
        <h3>📍 ADRESSE DE LIVRAISON</h3>
        <p>${order.shipping_address.name || ''}</p>
        <p>${order.shipping_address.street || order.shipping_address.address || ''}</p>
        <p>${order.shipping_address.city || ''}, ${order.shipping_address.province || ''} ${order.shipping_address.postal_code || ''}</p>
        ${order.shipping_address.phone ? `<p>📞 ${order.shipping_address.phone}</p>` : ''}
      </div>
      ` : ''}
      
      ${order.tracking_pin ? `
      <div class="shipping-info">
        <h3>📦 SUIVI DE LIVRAISON</h3>
        <p><strong>N° de suivi:</strong> ${order.tracking_pin}</p>
        ${order.canada_post_service_name ? `<p><strong>Service:</strong> ${order.canada_post_service_name}</p>` : ''}
      </div>
      ` : ''}
    </div>
    <div class="footer">
      <p class="gold">✨ Merci pour votre achat! ✨</p>
      <p>ALINA ELEGANCE - Ottawa-Gatineau, Canada</p>
      <p>www.alinaelegance.ca | alinaelegance1@gmail.com</p>
    </div>
  </div>
</body>
</html>`;

    const blob = new Blob([receiptHTML], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `recu_alina_${order.order_id.slice(-8)}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8 px-4">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-2xl overflow-hidden my-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-black to-gray-800 text-center py-8 relative">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-white/60 hover:text-white text-2xl"
          >
            ×
          </button>
          <h1 className="text-3xl font-bold text-brand-gold tracking-[4px]">ALINA ELEGANCE</h1>
          <p className="text-white/60 text-sm tracking-[2px] mt-2">REÇU DE COMMANDE</p>
        </div>
        
        {/* Gold Bar */}
        <div className="h-1 bg-gradient-to-r from-brand-gold via-yellow-200 to-brand-gold"></div>
        
        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Order Info */}
          <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-brand-gold">
            <h2 className="text-xs font-bold text-brand-black tracking-[2px] mb-3">INFORMATIONS DE COMMANDE</h2>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <p className="text-gray-600"><strong>N° Commande:</strong></p>
              <p className="text-brand-black font-mono">#{order.order_id.slice(-8).toUpperCase()}</p>
              <p className="text-gray-600"><strong>Date:</strong></p>
              <p className="text-brand-black">{new Date(order.created_at).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              <p className="text-gray-600"><strong>Statut:</strong></p>
              <Badge className={`${statusColors[order.order_status]} w-fit`}>{statusLabels[order.order_status]}</Badge>
              <p className="text-gray-600"><strong>Paiement:</strong></p>
              <p className="text-brand-black">{paymentLabels[order.payment_status] || order.payment_status}</p>
            </div>
          </div>
          
          {/* Items */}
          <div>
            <h2 className="text-xs font-bold text-brand-gold tracking-[2px] mb-4 pb-2 border-b-2 border-brand-gold">ARTICLES COMMANDÉS</h2>
            <div className="space-y-3">
              {order.items.map((item, i) => (
                <div key={i} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <img 
                    src={item.image?.startsWith('/api') ? `${API_URL}${item.image}` : item.image || 'https://via.placeholder.com/60'} 
                    alt={item.name}
                    className="w-16 h-16 object-cover rounded"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-brand-black">{item.name}</p>
                    <p className="text-sm text-gray-500">Qté: {item.quantity} × ${item.price.toFixed(2)}</p>
                  </div>
                  <p className="text-brand-gold font-bold">${(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>
          </div>
          
          {/* Totals */}
          <div className="bg-brand-black p-5 rounded-lg text-white">
            <div className="flex justify-between py-2">
              <span className="text-white/70">Sous-total</span>
              <span>${order.subtotal?.toFixed(2) || '0.00'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-white/70">Livraison</span>
              <span>${order.delivery_fee?.toFixed(2) || '0.00'}</span>
            </div>
            <div className="flex justify-between py-3 border-t-2 border-brand-gold mt-2 text-lg">
              <span className="font-bold">TOTAL</span>
              <span className="font-bold text-brand-gold">${order.total.toFixed(2)} CAD</span>
            </div>
          </div>
          
          {/* Shipping Address */}
          {order.shipping_address && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-xs font-bold text-brand-black tracking-[1px] mb-3 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-brand-gold" />
                ADRESSE DE LIVRAISON
              </h3>
              <div className="text-sm text-gray-700 space-y-1">
                <p className="font-medium">{order.shipping_address.name || ''}</p>
                <p>{order.shipping_address.street || order.shipping_address.address || ''}</p>
                <p>{order.shipping_address.city || ''}, {order.shipping_address.province || ''} {order.shipping_address.postal_code || ''}</p>
                {order.shipping_address.phone && <p>📞 {order.shipping_address.phone}</p>}
              </div>
            </div>
          )}
          
          {/* Tracking */}
          {order.tracking_pin && (
            <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
              <h3 className="text-xs font-bold text-cyan-800 tracking-[1px] mb-3 flex items-center gap-2">
                <Truck className="h-4 w-4" />
                SUIVI DE LIVRAISON
              </h3>
              <p className="font-mono text-lg text-cyan-900">{order.tracking_pin}</p>
              {order.canada_post_service_name && <p className="text-sm text-cyan-700 mt-1">{order.canada_post_service_name}</p>}
              <a 
                href={`https://www.canadapost-postescanada.ca/track-reperage/fr#/search?searchFor=${order.tracking_pin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-3 text-sm text-cyan-600 hover:text-cyan-800 font-medium"
              >
                Suivre sur Canada Post →
              </a>
            </div>
          )}
          
          {/* Download Button */}
          <Button 
            onClick={downloadReceipt}
            className="w-full bg-brand-black hover:bg-brand-black/90 text-white py-6"
          >
            <Download className="h-5 w-5 mr-2" />
            Télécharger le reçu (PDF)
          </Button>
        </div>
        
        {/* Footer */}
        <div className="bg-brand-black text-center py-4 text-sm">
          <p className="text-brand-gold">✨ Merci pour votre achat! ✨</p>
          <p className="text-white/50 mt-1">www.alinaelegance.ca</p>
        </div>
      </div>
    </div>
  );
};

export default function OrdersPage() {
  const { t } = useTranslation();
  const { isAuthenticated, openAuthModal } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  const { isSupported, isSubscribed, subscribe, deviceType } = usePushNotifications();

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated]);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setOrders(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleEnableNotifications = async () => {
    const success = await subscribe();
    if (success) {
      toast.success('Notifications activées! Vous serez informé de chaque mise à jour de vos commandes.');
    } else {
      if (deviceType === 'ios') {
        toast.error('Sur iPhone, ajoutez d\'abord ce site à votre écran d\'accueil via Safari.');
      } else {
        toast.error('Impossible d\'activer les notifications. Vérifiez les permissions de votre navigateur.');
      }
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-28 pb-20 bg-brand-white flex flex-col items-center justify-center px-4">
        <Package className="h-16 w-16 text-brand-gold/50 mb-4" />
        <h1 className="font-heading text-3xl text-brand-black mb-4 text-center">{t('orders.myOrders')}</h1>
        <p className="text-brand-dark/60 mb-6 text-center">Connectez-vous pour voir vos commandes</p>
        <Button onClick={() => openAuthModal('login')} className="bg-brand-gold text-white hover:bg-brand-gold">
          {t('nav.login')}
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen pt-28 pb-20 bg-brand-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-20 bg-brand-white" data-testid="orders-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <span className="font-accent text-brand-gold text-sm tracking-[0.3em] uppercase">Mon compte</span>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-brand-black mt-2">{t('orders.myOrders')}</h1>
          </div>
          
          {/* Notification Toggle for Customers */}
          {isSupported && !isSubscribed && (
            <Button
              onClick={handleEnableNotifications}
              variant="outline"
              className="flex items-center gap-2 border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-white"
            >
              <Bell className="h-4 w-4" />
              <span>Activer les notifications</span>
            </Button>
          )}
          {isSubscribed && (
            <div className="flex items-center gap-2 text-green-600 text-sm">
              <Bell className="h-4 w-4" />
              <span>Notifications activées</span>
            </div>
          )}
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-16 bg-brand-cream rounded-lg">
            <Package className="h-16 w-16 text-brand-gold/50 mx-auto mb-4" />
            <p className="text-brand-dark/60 text-lg">{t('orders.noOrders')}</p>
            <Link to="/products">
              <Button className="mt-6 bg-brand-gold text-white hover:bg-brand-gold">
                Découvrir nos produits
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div
                key={order.order_id}
                className="bg-brand-cream border border-brand-gold/20 rounded-lg overflow-hidden hover:border-brand-gold/40 transition-all"
              >
                {/* Order Header */}
                <div className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="font-heading text-lg font-bold text-brand-black">
                          #{order.order_id.slice(-8).toUpperCase()}
                        </span>
                        <Badge className={`${statusColors[order.order_status]} border text-xs`}>
                          {statusLabels[order.order_status]}
                        </Badge>
                        {order.payment_status === 'paid' && (
                          <Badge className="bg-green-100 text-green-700 border-green-300 border text-xs">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Payé
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-brand-dark/60">
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {new Date(order.created_at).toLocaleDateString('fr-CA')}
                        </span>
                        <span className="font-semibold text-brand-gold">${order.total.toFixed(2)} CAD</span>
                      </div>
                    </div>
                    
                    <Button
                      onClick={() => setSelectedOrder(order)}
                      className="bg-brand-black text-white hover:bg-brand-black/90 flex items-center gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      Voir le reçu
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Order Items Preview */}
                  <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                    {order.items.slice(0, 4).map((item, i) => (
                      <img 
                        key={i}
                        src={item.image?.startsWith('/api') ? `${API_URL}${item.image}` : item.image || 'https://via.placeholder.com/60'} 
                        alt={item.name}
                        className="w-14 h-14 object-cover flex-shrink-0 rounded border border-brand-gold/20"
                      />
                    ))}
                    {order.items.length > 4 && (
                      <div className="w-14 h-14 bg-brand-gold/10 rounded flex items-center justify-center text-brand-gold text-sm font-bold">
                        +{order.items.length - 4}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Tracking Bar */}
                {order.tracking_pin && (
                  <div className="bg-cyan-50 px-4 sm:px-6 py-3 border-t border-cyan-200 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-cyan-800">
                      <Truck className="h-4 w-4" />
                      <span>Suivi: <span className="font-mono font-bold">{order.tracking_pin}</span></span>
                    </div>
                    <a 
                      href={`https://www.canadapost-postescanada.ca/track-reperage/fr#/search?searchFor=${order.tracking_pin}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-cyan-600 hover:text-cyan-800 font-medium"
                    >
                      Suivre →
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Order Receipt Modal */}
      {selectedOrder && (
        <OrderReceipt order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}
    </div>
  );
}
